import React, { useEffect, useImperativeHandle, useState, useContext } from "react";
import cx from "classnames";
import { IQuestionOptionImage } from "../interfaces/IDigitalAdviceData";
import styles from "../scss/DapFrame.module.scss";
import { AdviceControllerContext } from "../providers/DigitalAdviceProvider";

interface IDigitalAdviceQuestionFrame {
    images?: IQuestionOptionImage[];
    label?: string;
    hidePreload?: boolean;
}

const DigitalAdviceQuestionFrame = React.forwardRef(
    (props: IDigitalAdviceQuestionFrame, ref) => {
        const { images, label } = props;
        const { actions, selectors } = useContext(AdviceControllerContext);

        const [showFrame, setShowFrame] = useState(false);
        const [baseImagesList, setBaseImagesList] = useState([]);
        const [placeholderImagesList, setPlaceholderImagesList] = useState([]);
        const [activeImagesList, setActiveImagesList] = useState([]);

        useImperativeHandle(ref, () => ({
            addImages: (image: IQuestionOptionImage, isAdded: boolean = false) => {
                if (!image || !image.imgUrl) return;
                let imagesList = [];
                if (isAdded) imagesList = [...activeImagesList, image];
                else
                    imagesList = activeImagesList.filter(
                        (img) => img.imgUrl !== image.imgUrl
                    );
                setActiveImagesList(imagesList);
            },
            addMultipleImages: (
                images: IQuestionOptionImage[],
                isAdded: boolean = false
            ) => {
                if (!(images && images.length && images[0].imgUrl)) return;
                let imagesList = [];
                if (isAdded) imagesList = [...activeImagesList, ...images];
                else {
                    imagesList = activeImagesList.reduce((acc, item) => {
                        const imageUrl = images.find((i) => i.imgUrl === item.imgUrl);
                        if (!(imageUrl && imageUrl.imgUrl)) {
                            acc = [...acc, item];
                        }
                        return acc;
                    }, []);
                }
                setActiveImagesList(imagesList);
            },
            setImagePlaceholders: (images: IQuestionOptionImage[]) => {
                setPlaceholderImagesList(images);
            },
            resetPlaceholder: () => {
                setPlaceholderImagesList([]);
            },
            setImagesList: (images: IQuestionOptionImage[]) => {
                setActiveImagesList(images);
            },
            getFrameImages: () => {
                return activeImagesList || [];
            },
            resetFrameImages: () => {
                setActiveImagesList([]);
            }
        }));

        const resetFrameImages = (baseImgs): void => {
            if (
                !!(baseImgs && baseImgs.length) &&
                !!(baseImagesList && baseImagesList.length)
            ) {
                if (baseImgs[0].imgUrl !== baseImagesList[0].imgUrl) {
                    setActiveImagesList([]);
                }
            }
        };

        const filterUniqueActiveImages = (
            images: IQuestionOptionImage[]
        ): IQuestionOptionImage[] => {
            let newListImages = [];
            let count = 0;
            let start = false;

            for (let j = 0; j < images.length; j++) {
                for (let k = 0; k < newListImages.length; k++) {
                    if (images[j].imgUrl === newListImages[k].imgUrl) {
                        start = true;
                    }
                }
                count++;
                if (count == 1 && start == false) {
                    newListImages.push(images[j]);
                }
                start = false;
                count = 0;
            }
            return newListImages;
        };

        const setBaseIMG = (images: IQuestionOptionImage[]) => {
            setShowFrame(!!(images && images.length));
            resetFrameImages(images);
            setBaseImagesList(images);
        };

        useEffect(() => {
            if (props.hidePreload) setBaseIMG(images);
        }, [images, props.hidePreload]);

        return (
            <React.Fragment>
                {!props.hidePreload ? (
                    <div
                        className={cx([
                            styles["frame-preload"],
                            styles["pre-loader"]
                        ])}></div>
                ) : (
                    <div
                        className={styles["question-frame-wrapper"]}
                        style={{
                            display: showFrame ? "flex" : "none"
                        }}>
                        <div className={cx(styles["question-frame-container"])}>
                            {baseImagesList
                                .concat(placeholderImagesList)
                                .concat(filterUniqueActiveImages(activeImagesList))
                                .map((img, idx) => (
                                    <img
                                        src={img.imgUrl}
                                        style={{
                                            zIndex: img.zIndex
                                        }}
                                        key={`${img.id}${idx}`}
                                        alt="img"
                                        width="100%"
                                    />
                                ))}
                        </div>
                        {label && (
                            <div className={styles["question-frame-label"]}>
                                <p className={styles["question-frame--label"]}>{label}</p>
                            </div>
                        )}
                    </div>
                )}
            </React.Fragment>
        );
    }
);

export default DigitalAdviceQuestionFrame;
